/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Form,
  Input,
  Button,
  Row,
  Col,
  Spin,
  Card,
  DatePicker,
  Tabs,
  message,
  Typography,
} from 'antd';
import {
  InfoCircleOutlined,
} from '@ant-design/icons';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import Select from 'components/Select';
import { onError } from 'utils/handlers';
import {
  deleteExpedienteDeLicencia,
  getExpedienteDeLicencia,
  getExpedientesDeLicencias,
  patchExpedienteDeLicencia,
  postExpedienteDeLicencia,
} from 'api/empresas/expedienteDeLicenciaDeFuncionamiento';
import { DATETIME_FRONTEND_FORMAT } from 'utils/formatValues';
import { ActionsNames, hasActionPermission } from 'utils/permission/index';
import { getGirosLicencias } from 'api/empresas/giroDeLicencia';
import { getCategoriasDeGiro } from 'api/empresas/categoriaDeGiroDeLicencia';
import { getEmpresa } from 'api/empresas/empresas';
import { InputSearchPro } from 'components/InputSearch';
import BusquedaAvanzadaPadrones from 'views/Recaudacion/Caja/BusquedaAvanzadaPadrones';
import TextArea from 'antd/lib/input/TextArea';
import { getEmpresaCaja } from 'api/empresas';

const { TabPane } = Tabs;
const { Text, Title } = Typography;
const baseURI = 'empresas/expedientes-de-licencias/';

const models = ['expedientedelicenciafuncionamiento'];

const ExpedientesDeLicencia = ({ permission }) => {
  const estadosGlobales = useSelector(({ catalogs }) => catalogs.estadosGlobales);
  const [form] = Form.useForm();
  const [formInfo] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState({ results: [] });
  const [currentPage, setCurrentPage] = useState(1);
  const [hasAllViewPermission, setHasAllViewPermission] = useState(true);
  const [tipoDeGiro, setTipoDeGiro] = useState([]);
  const [categoriaGiro, setCategoriaGiro] = useState([]);
  const [empresaFound, setEmpresaFound] = useState([]);
  const [visibleBusquedaAvanzada, setVisibleBusquedaAvanzada] = useState(false);
  // const [isEditing, setIsEditing] = useState(false);

  const fetchData = async (page = 1, q = null) => {
    setLoading(true);
    setCurrentPage(page);
    const _data = await getExpedientesDeLicencias({
      ...q,
      page,
    });
    setData(_data);
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    let _hasAllPermission = true;
    const validatePermission = () => {
      const _hasAllViewPermission = models
        .map((m) => hasActionPermission(m, ActionsNames.view))
        .every((e) => e);
      if (!_hasAllViewPermission) {
        message.warn('Hacen falta permisos en los catálogos relacionados a este módulo');
      }
      if (mounted) {
        _hasAllPermission = _hasAllViewPermission;
        setHasAllViewPermission(_hasAllViewPermission);
      }
    };
    const fetchAll = async () => {
      setLoading(true);
      const [_data, _tipoDeGiro, _categoriaGiro] = await Promise.all([
        getExpedientesDeLicencias({ page: 1 }, true),
        getGirosLicencias(),
        getCategoriasDeGiro(),
      ]);
      if (mounted) {
        setData(_data);
        setTipoDeGiro(_tipoDeGiro);
        setCategoriaGiro(_categoriaGiro);
        setLoading(false);
      }
    };
    validatePermission();
    if (_hasAllPermission) {
      fetchAll();
    }
    return () => { mounted = false; };
  }, []);

  const fetchEmpresa = async (id) => {
    try {
      setLoading(true);
      if (id) {
        const _empresa = await getEmpresa(id?.id || id);
        if (_empresa) {
          setEmpresaFound([_empresa]);
          setTimeout(() => {
            form.setFieldsValue({
              empresa: _empresa.id,
            });
          });
        } else {
          message.info('No se encontro la empresa');
        }
        setLoading(false);
      }
    } catch (error) {
      onError(error, setLoading);
    }
  };

  const resetAndValidate = () => {
    setEmpresaFound([]);
    form.resetFields(['q']);
    form.validateFields(['q']);
  };

  const onCancel = () => {
    setVisible(false);
    form.resetFields();
    setSelectedRowKeys([]);
    setEmpresaFound([]);
    // setIsEditing(false);
  };

  const onClickEdit = async (id) => {
    const key = selectedRowKeys[0] || id;
    const match = await getExpedienteDeLicencia(key);
    if (match) {
      await fetchEmpresa(match.empresa?.id);
      form.setFieldsValue(match);
    }
    setLoading(false);
    // setIsEditing(true);
    setVisible(true);
  };

  const onFinish = async (_continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      await formInfo.validateFields();
      const values = form.getFieldsValue();
      const [key] = selectedRowKeys;
      const _data = await (key ? patchExpedienteDeLicencia(key, values)
        : postExpedienteDeLicencia(values)
      );
      if (_data) {
        await fetchData();
        if (_continue) {
          if (!key) {
            setSelectedRowKeys([_data.id]);
          }
          await onClickEdit(_data.id);
        } else {
          onCancel();
        }
        setLoading(false);
        return _data;
      }
      setLoading(false);
      return true;
    } catch (err) {
      onError(err, setLoading);
      return false;
    }
  };

  const deleteItem = async () => {
    setLoading(true);
    if (selectedRowKeys.length) {
      const [key] = selectedRowKeys;
      const deleted = await deleteExpedienteDeLicencia(key);
      if (deleted) {
        setVisibleAlert(false);
        onCancel();
        await fetchData();
      }
    }
    setLoading(false);
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAdd = () => {
    onCancel();
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const searchEmpresa = async () => {
    try {
      setLoading(true);
      await form.validateFields(['q']);
      const { q } = form.getFieldsValue(['q']);
      if (q) {
        const _empresa = await getEmpresaCaja({ q });
        if (_empresa || _empresa?.length || _empresa?.results?.length) {
          const [selected] = (_empresa || _empresa?.length) ? _empresa : _empresa.results;
          form.setFieldsValue({
            empresa: selected.id,
          });
          setEmpresaFound([selected]);
        } else if (!_empresa) {
          message.info('No se encontró Empresa');
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const columns = [
    {
      titleText: 'Empresa',
      dataIndex: 'empresa',
      key: 'empresa',
      render: (val) => (val?.nombre_comercial),
      filterName: 'empresa__nombre_comercial',
      width: 200,
    },
    {
      titleText: 'Clave de control',
      dataIndex: 'clave_de_control',
      key: 'clave_de_control',
      width: 200,
    },
    {
      titleText: 'Giro',
      dataIndex: 'giro',
      key: 'giro',
      width: 200,
      catalog: {
        cat: tipoDeGiro,
        labelProp: 'descripcion',
        use: true,
      },
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 200,
      catalog: {
        cat: estadosGlobales,
        labelProp: 'descripcion',
      },
    },
  ];

  const rules = {
    required: [
      { required: true },
    ],
  };

  return (
    <Row align="center" justify="center" className="container">
      <Spin tip="Cargando..." spinning={loading}>
        {!visible
          ? (
            <Table
              cols={columns}
              mobileColumns={[columns[0], columns[1]]}
              data={data.results || []}
              rowSelection={rowSelection}
              handleOnRowClick={handleOnRowClick}
              controls={{
                onClickAdd,
                onClickEdit,
                onClickDelete,
              }}
              rowKey="id"
              baseURI={baseURI}
              permission={permission}
              pagination={false}
              paginationCallback={fetchData}
              count={data.count}
              currentPage={currentPage}
              hasAllViewPermission={hasAllViewPermission}
            />
          ) : (
            <Col span={24} style={{ height: '100%' }}>
              <Card
                className="form-container"
                title={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} expediente de licencias de funcionamiento`}
                extra={(
                  <FormSubmitControls
                    onFinish={onFinish}
                    onCancel={onCancel}
                    allowSaveAndContinue
                    onCancelText="Cancelar"
                    permission={permission}
                  />
                )}
                bordered={false}
              >
                <Tabs
                  type="card"
                >
                  <TabPane
                    tab={(
                      <span>
                        <InfoCircleOutlined />
                        Información General
                      </span>
                    )}
                    key="general"
                    forceRender
                  >
                    <Form
                      name="general"
                      layout="vertical"
                      form={form}
                      onFinish={() => onFinish()}
                      scrollToFirstError
                    >
                      <Row gutter={10}>
                        <InputSearchPro
                          tooltip="Búsqueda por Razon social"
                          label="Empresa"
                          onClickAdvanced={() => setVisibleBusquedaAvanzada(true)}
                          name="q"
                          rules={rules.required}
                          breakPoints={{
                            xs: 24,
                            sm: 24,
                            md: 16,
                          }}
                          inputProps={{
                            onPressEnter: () => searchEmpresa(),
                            onChange: ({ target: { value } }) => {
                              if (!value) {
                                resetAndValidate();
                              }
                            },
                          }}
                          resultProps={{
                            breakPoints: {
                              xs: 24,
                              sm: 24,
                              md: 16,
                            },
                            name: 'empresa',
                            label: 'Empresa',
                            dataSource: empresaFound,
                            labelProp: 'nombre_comercial',
                            onClear: resetAndValidate,
                          }}
                        />
                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            name="clave_de_control"
                            label="clave de control"
                            hasFeedback
                          >
                            <Input disabled allowClear />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            name="giro"
                            label="Giro de licencia"
                            hasFeedback
                            required
                          >
                            <Select
                              keyLabelRender
                              dataSource={tipoDeGiro}
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            name="categoria_de_giro"
                            label="Categoria de Giro de licencia"
                            hasFeedback
                            required
                          >
                            <Select dataSource={categoriaGiro} />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            name="aforo"
                            label="Aforo"
                            hasFeedback
                          >
                            <Input allowClear />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            name="fecha_de_registro_de_licencia"
                            label="fecha de registro de licencia"
                            hasFeedback
                            required
                          >
                            <DatePicker format={DATETIME_FRONTEND_FORMAT} />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            name="fecha_de_renovacion_de_licencia"
                            label="fecha de renovacion de licencia"
                            hasFeedback
                            required
                          >
                            <DatePicker format={DATETIME_FRONTEND_FORMAT} />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            name="zona"
                            label="Zonificación"
                            hasFeedback
                          >
                            <Input allowClear />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            name="sector"
                            label="Sector"
                            hasFeedback
                          >
                            <Input allowClear />
                          </Form.Item>
                        </Col>
                        <Col lg={8} md={16} sm={24} xs={24}>
                          <Form.Item
                            name="inspeccion"
                            label="¿Inspeccion?"
                            hasFeedback
                          >
                            <Select trueFalse />
                          </Form.Item>
                        </Col>
                        <Col lg={8} md={16} sm={24} xs={24}>
                          <Form.Item
                            name="dictamen"
                            label="Dictamen"
                            hasFeedback
                          >
                            <Select trueFalse />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            name="estados_globales"
                            label="Estatus"
                            hasFeedback
                          >
                            <Select
                              dataSource={estadosGlobales}
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            name="descripcion_de_actividad"
                            label="Descripcion de actividad"
                            hasFeedback
                          >
                            <TextArea
                              allowClear
                              autoSize={{ minRows: 3, maxRows: 6 }}
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            name="observaciones"
                            label="Observaciones"
                            hasFeedback
                          >
                            <TextArea
                              allowClear
                              autoSize={{ minRows: 3, maxRows: 6 }}
                            />
                          </Form.Item>
                        </Col>
                        <Col span={24}>
                          <Title level={4}>
                            <Text>
                              Condicionantes
                            </Text>
                          </Title>
                        </Col>
                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            name="cumple_proteccion_civil"
                            label="¿Cumple proteccion civil?"
                            hasFeedback
                          >
                            <Select trueFalse />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            name="fecha_de_proteccion_civil"
                            label="Fecha de proteccion civil"
                            hasFeedback
                          >
                            <DatePicker format={DATETIME_FRONTEND_FORMAT} />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            name="cumple_condicion_estructural"
                            label="¿Cumple condicion estructural?"
                            hasFeedback
                          >
                            <Select trueFalse />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            name="fecha_de_condicion_estructural"
                            label="fecha de condicion estructural"
                            hasFeedback
                          >
                            <DatePicker format={DATETIME_FRONTEND_FORMAT} />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            name="cumple_licencia_sanitaria"
                            label="¿Cumple con fecha de licencia sanitaria?"
                            hasFeedback
                          >
                            <Select trueFalse />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            name="fecha_de_licencia_sanitaria"
                            label="fecha de licencia sanitaria"
                            hasFeedback
                          >
                            <DatePicker format={DATETIME_FRONTEND_FORMAT} />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            name="cumple_aviso_de_funcionamiento"
                            label="¿Cumple con aviso de funcionamiento?"
                            hasFeedback
                          >
                            <Select trueFalse />
                          </Form.Item>
                        </Col>
                        {/* {cumpleAvisoDeFuncionamiento && ( */}
                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            name="fecha_de_aviso_de_funcionamiento"
                            label="fecha de aviso de funcionamiento"
                            hasFeedback
                          >
                            <DatePicker format={DATETIME_FRONTEND_FORMAT} />
                          </Form.Item>
                        </Col>
                        {/* )} */}
                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            name="cumple_uso_de_suelo"
                            label="¿Cumple con uso de suelo?"
                            hasFeedback
                          >
                            <Select trueFalse />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            name="fecha_de_uso_de_suelo"
                            label="fecha de uso de suelo"
                            hasFeedback
                          >
                            <DatePicker format={DATETIME_FRONTEND_FORMAT} />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            name="uso_de_suelo"
                            label="Uso de Suelo"
                            hasFeedback
                          >
                            <Input allowClear />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Form.Item hidden>
                        <Button htmlType="submit" />
                      </Form.Item>
                    </Form>
                  </TabPane>
                </Tabs>
              </Card>
            </Col>
          )}
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          visible={visibleAlert}
          content="Expediente de licencia seleccionada"
          loading={loading}
        />
        <BusquedaAvanzadaPadrones
          visible={visibleBusquedaAvanzada}
          setVisible={setVisibleBusquedaAvanzada}
          callback={fetchEmpresa}
          tipoDePadron={2}
          allowAdd={false}
        />
      </Spin>
    </Row>
  );
};

export default ExpedientesDeLicencia;
